import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/DropdownMenu'
import { Check, ChevronDown, Clock, ExternalLink } from 'lucide-react'
import { PlatformIcon, PlatformIconStack, platformLabel } from 'components/PlatformIcon'
import { cn } from 'lib/utils'
import { useAnalytics, useIsMobile } from 'hooks'
import { useScheduleTimezone } from '../../common/hooks/use-brand-timezone'
import { formatInBusinessTz } from '../../common/utils/business-timezone'
import { AssetMedia, creativeToMediaSource, GeneratingMediaOverlay } from './AssetMedia'
import { EditsApplyingBadge } from './EditsApplyingBadge'
import { toGeneratingShape } from './GeneratingStatusText'
import {
  POST_CARD_MENU_ITEM_CLASS,
  PostCardFailedChip,
  PostCardHoverScrim,
  PostCardMenu,
  PostCardShapeBadge,
  SHAPE_LABEL,
} from './post-card-chips'
import { isSyntheticPostIdea } from '../selectors/posts-page-items'
import type { ContentShape, LivePlatform, PostIdea } from '../store/idea-store'

/**
 * Top-right status chip. Persistent but compact: at rest it's an iconish
 * capsule (a clock for scheduled, a pulsing dot for live/publishing); on card
 * hover it expands to the full label (e.g. the scheduled date/time). The width
 * animation is driven by the parent card's `group` hover.
 */
function IdeaCardStatusChip({
  isLive,
  isPublishing,
  scheduledLabel,
  isMobile,
}: {
  isLive: boolean
  isPublishing: boolean
  scheduledLabel: string
  /** Touch devices have no hover — keep the label expanded at rest. */
  isMobile?: boolean
}) {
  const isScheduled = !isLive && !isPublishing
  const label = isPublishing ? 'Publishing…' : isLive ? 'Live' : scheduledLabel

  return (
    <div
      // Solid dark capsule (mirrors the shape badge) so it reads on any image,
      // even at rest with no hover scrim behind it. Fixed height + matching
      // min-width keep it a perfect circle at rest (the icon line-height would
      // otherwise make it oval); it grows into a pill as the label expands on
      // hover. The leading glyph carries the state color: acid clock for
      // scheduled, pulsing dot for live.
      className="mono inline-flex h-8 min-w-[32px] items-center justify-center rounded-full border border-line bg-bg-2 px-1.5 text-[11px] font-medium text-paper shadow-sm"
    >
      {isScheduled ? (
        <Clock size={12} className="shrink-0 text-acid" />
      ) : (
        <span className="liveDot shrink-0" aria-hidden />
      )}
      <span
        className={cn(
          'max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-200 group-hover:max-w-[160px] group-hover:pl-1.5 group-hover:opacity-100',
          isMobile && 'max-w-[160px] pl-1.5 opacity-100'
        )}
      >
        {label}
      </span>
    </div>
  )
}

/**
 * Top-right "Needs approval" chip — a second indicator that sits alongside the
 * scheduled clock chip (never replacing it) on review-mode posts still awaiting
 * the user's sign-off. Acid-filled (inverted from the neutral dark status chip)
 * so it reads as an action-needed flag, and expands to its label on card hover
 * like the other chips. There's deliberately no Approve button here — clicking
 * the card opens PostModal, which carries the acid "Approve · {date}" CTA.
 */
function IdeaCardApprovalChip({ isMobile }: { isMobile?: boolean }) {
  return (
    <div
      className="mono text-ink-on-acid inline-flex h-8 min-w-[32px] items-center justify-center rounded-full bg-acid px-1.5 text-[11px] font-semibold shadow-sm"
      title="Needs approval"
    >
      <Check size={12} className="shrink-0" />
      <span
        className={cn(
          'max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-200 group-hover:max-w-[160px] group-hover:pl-1.5 group-hover:opacity-100',
          isMobile && 'max-w-[160px] pl-1.5 opacity-100'
        )}
      >
        Needs approval
      </span>
    </div>
  )
}

/** A published platform with a reachable permalink. */
interface LiveLink {
  platform: LivePlatform
  permalink: string
}

/**
 * Bottom-left "Visit" affordance for live posts (Pinterest's "Visit site"
 * slot). One published platform → a pill that links straight to the post.
 * Multiple platforms → the pill becomes a dropdown so the user picks which
 * platform to open. Hover-revealed; clicks never bubble to the card's onClick.
 */
function IdeaCardVisit({ links, isMobile }: { links: LiveLink[]; isMobile?: boolean }) {
  const pill = cn(
    'mono inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-full border border-line bg-bg-2 px-3 text-[11px] font-medium text-paper opacity-0 shadow-sm transition-opacity group-hover:opacity-100 hover:bg-bg-3',
    // Touch devices have no hover — keep the live-post Visit affordance visible.
    isMobile && 'opacity-100'
  )

  if (links.length === 1) {
    const { platform, permalink } = links[0]
    return (
      <a
        href={permalink}
        target="_blank"
        rel="noopener noreferrer"
        onClick={e => e.stopPropagation()}
        className={pill}
      >
        <PlatformIcon channel={platform} size={14} />
        View on {platformLabel(platform)}
        <ExternalLink size={12} />
      </a>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="View published post"
          onClick={e => e.stopPropagation()}
          className={`${pill} data-[state=open]:opacity-100`}
        >
          <PlatformIconStack channels={links.map(l => l.platform)} size={14} />
          <ChevronDown size={12} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        variant="dark"
        align="start"
        side="top"
        className="w-48"
        onClick={e => e.stopPropagation()}
      >
        {links.map(({ platform, permalink }) => (
          <DropdownMenuItem key={platform} asChild className={POST_CARD_MENU_ITEM_CLASS}>
            <a href={permalink} target="_blank" rel="noopener noreferrer">
              <PlatformIcon channel={platform} size={14} className="mr-2" />
              View on {platformLabel(platform)}
              <ExternalLink size={12} className="ml-auto" />
            </a>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface IdeaCardProps {
  idea: PostIdea
  /** Opens PostModal — the single hub for schedule/approve/view-live. */
  onPost: (idea: PostIdea) => void
  /** Retry path used by AssetMedia for failed video/image generation. */
  onRegenerate: (idea: PostIdea) => void
  /**
   * When provided (and the post has a real project), the ••• menu shows an Edit
   * action that routes to the post-aware Composer. Parent owns navigation.
   */
  onEdit?: () => void
  /**
   * When provided (and the post isn't already live/publishing), the ••• menu
   * shows a Delete action. The parent owns the confirmation + delete.
   */
  onDelete?: () => void
}

/** Compact scheduled-time label in the business timezone, e.g. "Jun 28, 9:00 AM". */
function formatScheduled(iso: string | undefined, timezone: string): string {
  if (!iso) return ''
  return formatInBusinessTz(iso, timezone, 'MMM d, h:mm a')
}

/**
 * A post rendered as a clickable media tile. The whole card opens PostModal
 * (the schedule/approve/view-live hub). At rest the media is clean save for two
 * persistent corner chips — the shape badge (top-left) and, when scheduled or
 * live, the status chip (top-right). On hover, Pinterest-style: gradient scrims
 * fade in between the media and the controls and the status chip expands to its
 * full label. Pre-publish posts reveal a ••• menu (bottom-right) with Edit +
 * Delete; live posts instead reveal a "Visit" affordance (bottom-left) linking
 * out to the published post(s).
 */
export function IdeaCard({ idea, onPost, onRegenerate, onEdit, onDelete }: IdeaCardProps) {
  const { track } = useAnalytics()
  const timezone = useScheduleTimezone()
  // No hover on touch — surface the otherwise hover-revealed controls (••• menu,
  // Visit, status-chip labels) so they stay reachable / legible at rest.
  const isMobile = useIsMobile()

  const handleOpenPost = () => {
    track(AnalyticsEvents.Home.HOME_IDEA_CARD_TAPPED, {
      topic_id: idea.topicId ?? undefined,
      idea_id: idea.projectId,
      shape: idea.shape,
      status: idea.status,
    })
    onPost(idea)
  }

  const isLive = idea.status === 'live'
  const isPublishing = idea.status === 'publishing'
  const isScheduled = idea.status === 'scheduled'
  const showStatus = isLive || isPublishing || isScheduled
  // Review-mode post still awaiting sign-off (mirrors PostModal's gate:
  // requiresApproval && !approvedAt). Surfaced as a second chip beside the clock.
  const needsApproval = isScheduled && !!idea.requiresApproval && !idea.approvedAt

  // Synthetic ideas wrap a standalone Creative — no project to open in Composer.
  const isSynthetic = isSyntheticPostIdea(idea.projectId)
  // Edit and Delete only apply before a post goes out — once live (or mid-
  // publish) there's nothing to edit or delete, only the live post to visit.
  const canEdit = !!onEdit && !isSynthetic && !isLive && !isPublishing
  const canDelete = !!onDelete && !isLive && !isPublishing
  const showMenu = canEdit || canDelete

  // Live posts get a "Visit" affordance instead of the menu — one link per
  // platform that actually published with a reachable permalink.
  const liveLinks: LiveLink[] = (
    Object.entries(idea.platformResults ?? {}) as Array<
      [LivePlatform, NonNullable<PostIdea['platformResults']>[LivePlatform]]
    >
  )
    .filter(([, r]) => r?.state === 'success' && !!r?.permalink)
    .map(([platform, r]) => ({ platform, permalink: r!.permalink as string }))
  const showVisit = isLive && liveLinks.length > 0

  // Anything anchored along the bottom edge wants the bottom scrim behind it.
  const showBottomScrim = showMenu || showVisit

  // A playable reel shows a mute chip in the bottom-right corner — the same slot
  // the ••• menu lives in. When both are present, lift the ••• above the chip on
  // hover (the chip itself never moves) so they stack instead of overlapping.
  const mediaSource = creativeToMediaSource(idea.primaryCreative)
  const hasMuteChip = idea.shape === 'short_vertical_video' && !!mediaSource.videoUrl

  // The tile's thumbnail is the last-RENDERED master, which lags the user's
  // composition edits until the server re-renders it — chip it so the stale
  // image never reads as a revert. Pre-publish only (published masters are
  // final; live posts also own the bottom-left slot with Visit).
  const isEditsApplying =
    idea.primaryCreative?.content?.compositionPendingRender === true &&
    !isLive &&
    !isPublishing &&
    !idea.isRegenerating

  // Home surface's take on the failure treatment. Single-platform per idea on
  // this card (the primary drives the tile), so one FailedPlatform is enough;
  // the modal that opens carries the multi-platform detail. Key by the primary
  // creative id and use the idea's primary platform for the copy so the
  // popover reads correctly on any surface.
  const primaryFailure = idea.primaryCreative?.publishFailure
  const failedPlatformsForChip =
    primaryFailure && idea.primaryCreative
      ? [
          {
            creativeId: idea.primaryCreative.id,
            platform: idea.primaryPlatform,
            reason: primaryFailure.reason,
          },
        ]
      : []
  const hasFailure = failedPlatformsForChip.length > 0

  return (
    <article
      onClick={handleOpenPost}
      className="group relative flex w-full cursor-pointer flex-col overflow-hidden rounded-[10px] border border-line bg-bg-2 transition-colors hover:border-line-bright"
    >
      <div className="relative aspect-square w-full overflow-hidden bg-bg-3">
        <AssetMedia
          media={mediaSource}
          shape={idea.shape}
          onRetry={() => onRegenerate(idea)}
          forceGenerating={idea.isRegenerating}
          className="absolute inset-0 h-full w-full"
        />

        {/* Hover scrims — Pinterest-style blackish gradient veil that sits
            between the media and the controls so chips/buttons read on any
            image. Top scrim (taller here) anchors the badges; the bottom scrim
            anchors the ••• menu / Visit. */}
        <PostCardHoverScrim edge="top" className="h-24" />
        {showBottomScrim && <PostCardHoverScrim edge="bottom" />}

        <PostCardShapeBadge shape={idea.shape} />

        {/* Status chip(s) — top-right, persistent, expand on hover. The
            scheduled/live clock chip stays as-is; a needs-approval post stacks a
            second acid "Needs approval" chip beneath it. When the post has a
            failed publish (primary creative's publishFailure marker), the
            destructive failed chip stacks alongside so the user can jump
            straight into the recovery flow. */}
        {(showStatus || hasFailure) && (
          <div className="absolute top-2 right-2 z-20 flex flex-col items-end gap-1.5">
            {showStatus && (
              <IdeaCardStatusChip
                isLive={isLive}
                isPublishing={isPublishing}
                scheduledLabel={formatScheduled(idea.scheduledAt, timezone)}
                isMobile={isMobile}
              />
            )}
            {needsApproval && <IdeaCardApprovalChip isMobile={isMobile} />}
            {hasFailure && (
              <PostCardFailedChip
                failedPlatforms={failedPlatformsForChip}
                onOpenPost={handleOpenPost}
              />
            )}
          </div>
        )}

        {/* Edits applying — bottom-left (the Visit slot, mutually exclusive:
            never live here). The master render is catching up to the edits. */}
        {isEditsApplying && (
          <EditsApplyingBadge overlay className="z-30" creativeId={idea.primaryCreative?.id} />
        )}

        {/* Visit — bottom-left, hover-revealed. The live post's primary action:
            jump to the published post (Pinterest's "Visit site" slot). */}
        {showVisit && (
          <div className="absolute bottom-2 left-2 z-30">
            <IdeaCardVisit links={liveLinks} isMobile={isMobile} />
          </div>
        )}

        {/* ••• menu — bottom-right, hover-revealed (the Pinterest action slot).
            Opens upward so it doesn't collide with the card edge. Only on
            pre-publish posts (draft/scheduled); live posts show Visit instead.
            Lifts above the reel mute chip when both share the corner. */}
        {showMenu && (
          <PostCardMenu
            canEdit={canEdit}
            canDelete={canDelete}
            onEdit={onEdit}
            onDelete={onDelete}
            triggerClassName={cn(
              'absolute right-2 z-30 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-line bg-bg-2 text-paper opacity-0 shadow-sm transition-opacity group-hover:opacity-100 hover:bg-bg-3 data-[state=open]:opacity-100',
              hasMuteChip ? 'bottom-12' : 'bottom-2',
              // No hover on touch — keep the ••• (Edit/Delete) reachable at rest.
              isMobile && 'opacity-100'
            )}
          />
        )}
      </div>
    </article>
  )
}

interface IdeaCardSkeletonProps {
  shape?: ContentShape
}

export function IdeaCardSkeleton({ shape = 'static_image' }: IdeaCardSkeletonProps) {
  const shapeLabel = SHAPE_LABEL[shape]

  return (
    <article
      aria-busy="true"
      aria-label={`Generating ${shapeLabel.toLowerCase()}`}
      className="relative flex w-full flex-col overflow-hidden rounded-[10px] border border-line bg-bg-2"
    >
      <div className="relative aspect-square w-full overflow-hidden bg-bg-3">
        <GeneratingMediaOverlay shape={toGeneratingShape(shape)} radius={10} />
        <PostCardShapeBadge shape={shape} />
      </div>
    </article>
  )
}
